import React from "react";
import {LucideLock} from "lucide-react";
import { Typography } from "../ui/Typography";
import {Input} from "../ui/Input";
import { SelectOptions } from "../ui/Select";
import {Button} from "../ui/Button";

export const AccountSettings = () => {
    return (
        <main className="flex-grow">
            
            <div id="library-header" className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-2">
                <Typography as="h1" variant="h1" className="text-surface-300">
                    Account Settings
                </Typography>

                <Typography as="p" variant="p" className="text-lg py-8">
                    Manage your password, addresses, and other account details here.
                </Typography>
                
                <AccountDetails />
                <Password />
            </div>
            
        </main>
    );
};

const AccountDetails = () => {
    const optionsArr = ['Canada','United Kingdom', 'United States', 'Other'];
    return (
        <section
            id="account-settings"
            aria-labelledby="account-heading"
            className="bg-surface-900 shadow-sm rounded-xl overflow-hidden mb-8 border border-surface-800"
        >
            <form id="account-form">
                <div className="p-6 sm:p-8">

                    <Typography as="h2" variant="h2">Account Details</Typography>

                    
                    <div className="mt-6 space-y-10">
                        <div>
                            <Typography as="h3" variant="h3">Personal Information</Typography>
                            
                            <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
                                <div className="sm:col-span-3">
                                    
                                    <Typography as="label" variant="label">First Name</Typography>
                                    
                                    <div className="mt-2">

                                        <Input
                                            type="text"
                                            name="first-name"
                                            id="first-name"
                                            placeholder="Bert"
                                            className="py-2 px-3 w-full "
                                        />
                                        
                                    </div>
                                </div>

                                <div className="sm:col-span-3">

                                    <Typography as="label" variant="label">Last Name</Typography>
                                    
                                    <div className="mt-2">

                                        <Input
                                            type="text"
                                            name="last-name"
                                            id="last-name"
                                            placeholder="Poepjes"
                                            className="py-2 px-3 w-full "
                                        />
                                        
                                    </div>
                                </div>

                                <div className="sm:col-span-6">

                                    <Typography as="label" variant="label">Email Address</Typography>
                                    
                                    <div className="relative mt-2">

                                        <Input
                                            intent="error"
                                            type="email"
                                            name="email"
                                            focus={false}
                                            id="email"
                                            placeholder="bert@shitgate.com"
                                            className="py-2 px-3 w-full"
                                            readOnly
                                        />
                                        
                                        <div
                                            className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
                                            title="This field cannot be edited."
                                        >
                                            <LucideLock className="h-5 w-5 text-surface-500" />
                                        </div>
                                    </div>
                                    <p className="mt-2 text-xs text-surface-500">
                                        To change your email address, please contact support. This
                                        is a security measure to protect your account.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Addresses */}
                        <div className="md:grid md:grid-cols-2 md:gap-x-12 space-y-10 md:space-y-0">
                            <div className="space-y-6">
                                
                                <Typography as="h3" variant="h3">Billing Address</Typography>
                                
                                <div className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
                                    <div className="sm:col-span-6">

                                        <Typography as="label" variant="label">Country</Typography>
                                        
                                        <div className="mt-2">
                                            <SelectOptions sizing="medium" className="block w-full"> 
                                                {optionsArr.map((opt) => <option key={opt}>{opt}</option>)}
                                            </SelectOptions>
                                        </div>
                                        
                                    </div>

                                    <div className="col-span-full">

                                        <Typography as="label" variant="label">Street address</Typography>
                                        <div className="mt-2">
                                            <Input
                                                type="text"
                                                name="shipping-address"
                                                id="shipping-address"
                                                placeholder="Rainbow Road 1"
                                                className="py-2 px-3 w-full"
                                            />
                                            
                                        </div>
                                    </div>
                                    <div className="sm:col-span-3 sm:col-start-1">

                                        <Typography as="label" variant="label">City</Typography>
                                        
                                        <div className="mt-2">
                                            
                                            <Input
                                                type="text"
                                                name="shipping-city"
                                                id="shipping-city"
                                                placeholder="Gotham"
                                                className="py-2 px-3 "
                                            />
                                            
                                        </div>
                                    </div>
                                    <div className="sm:col-span-3">
                                        
                                        <Typography as="label" variant="label">Zip code</Typography>
                                        
                                        <div className="mt-2">
                                            <Input
                                                type="text"
                                                name="zip-code"
                                                id="zip-code"
                                                placeholder="50000"
                                                className="py-2 px-3 "
                                            />
                                            
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                        </div>
                    </div>
                </div>
                
                <div className="flex items-center justify-end gap-x-6 bg-surface-800 px-6 py-4">

                    <Button
                        intent="primary"
                        disabled
                    >
                        Update Account
                    </Button>
                    
                </div>
                
                
            </form>
        </section>
    );
};

const Password = () => {
    return (
        <section
            id="security-settings"
            aria-labelledby="password-heading"
            className="bg-surface-900 shadow-sm rounded-xl overflow-hidden border border-surface-800 "
        >
            <form id="password-form">
                <div className="p-6 sm:p-8">
                    <h2 id="password-heading" className="text-xl font-semibold leading-7 text-surface-900">
                        Password
                    </h2>

                    <Typography as="h2" variant="h2">Password</Typography>
                    
                    <Typography as="p" variant="p" className="mt-1 text-sm leading-6 text-surface-500">
                        For your security, we recommend choosing a strong, unique password.
                    </Typography>

                    <div className="mt-6 grid grid-cols-1 gap-y-6">
                        <div>
                            
                            <Typography as="label" variant="label">Current Password</Typography>
                            
                            <div className="mt-3">
                                
                                <Input
                                    type="password"
                                    name="current-password"
                                    id="current-password"
                                    placeholder="••••••••••"
                                    autoComplete="current-password"
                                    className="w-full"
                                />
                                
                            </div>
                        </div>

                        <div>

                            <Typography as="label" variant="label">New Password</Typography>
                            
                            <div className="mt-3">
                                <Input
                                    type="password"
                                    name="new-password"
                                    id="new-password"
                                    placeholder="••••••••••"
                                    className="w-full"
                                />
                            </div>
                            
                            <Typography as="p" variant="p" className="text-xs mt-2">
                                Must be at least 8 characters long and contain a mix of letters, numbers, and symbols.
                            </Typography>
                            
                        </div>
                        
                        
                        <div>
                            <Typography as="label" variant="label">Confirm New Password</Typography>
                            <div className="mt-3">
                                <Input
                                    type="password"
                                    name="confirm-password"
                                    id="confirm-password"
                                    placeholder="••••••••••"
                                    className="w-full"
                                />
                            </div>
                            <p
                                id="password-match-error"
                                className="mt-2 text-xs text-error-500 hidden"
                            >
                                Passwords do not match.
                            </p>
                        </div>
                    </div>
                </div>
                <div
                    className="flex items-center justify-end gap-x-6 bg-surface-800 px-6 py-4"
                >
                    <Button 
                        intent="primary"
                        disabled
                    >
                        Change Password
                    </Button>

                </div>
            </form>
        </section>
    );
};


const PasswordStrengthMeter = () => {
    {/* DLC */}
    return (
        <div className="space-y-2">
            <div className="grid grid-cols-4 gap-2">
                <div
                    id="strength-bar-1"
                    className="h-1.5 rounded-full bg-surface-200"
                ></div>
                <div
                    id="strength-bar-2"
                    className="h-1.5 rounded-full bg-surface-200"
                ></div>
                <div
                    id="strength-bar-3"
                    className="h-1.5 rounded-full bg-surface-200"
                ></div>
                <div
                    id="strength-bar-4"
                    className="h-1.5 rounded-full bg-surface-500"
                ></div>
            </div>
            <p
                id="strength-text"
                className="text-xs font-medium text-surface-500"
            />
        </div>
    );
};





